import { useNavigate } from 'react-router-dom'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'

import { getErrorMessage } from '../../../../../../../../utils/getErrorMessage'
import { type ExpertDomain } from '../../../../../../../../types/domain/expert-domain'
import {
  TOAST_SUCCESS_DEFAULT_OPTIONS,
  TOAST_ERROR_DEFAULT_OPTIONS,
} from '../../../../../../../../constants/toast'
import { type FormsValues } from './types/FormsSteps'
import { internationalPersonSchema } from '../../../../../../../../validators/internationalPersonSchema'
import { internationalBankDataSchema } from '../../../../../../../../validators/internationalBankDataSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { type UpdateExpertInternationalParams } from '../../../../../../../../services/hooks/experts/mutations/useExpertInternationalDataMutation'
import { useQueryClient } from 'react-query'
import { useExpertMutation } from '../../../../../../../../services/hooks/experts/mutations/useExpertMutation'

export const useInternationalStepperContainer = (expert?: ExpertDomain) => {
  const [currentStep, setCurrentStep] = useState<0 | 1>(0)
  const queryClient = useQueryClient()

  const { formatMessage } = useIntl()
  const { mutate, isLoading: isExpertInternationalDataSubmitting } =
    useExpertMutation(expert!.id)

  const schema = useMemo(() => {
    if (currentStep === 0) {
      return internationalPersonSchema
    }

    return internationalBankDataSchema
  }, [currentStep])

  const navigate = useNavigate()

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      name: expert?.name,
      email: expert?.email,
      phone: expert?.phone,
      linkedin: expert?.linkedin,
      city: expert?.naturalData?.city,
      state: expert?.naturalData?.state,
      country: expert?.naturalData?.country,
      swiftCode: expert?.bankData?.swiftCode ?? '',
      bankAccountNumber: expert?.bankData?.bankAccountNumber ?? '',
      internationalBankBranch: expert?.bankData?.internationalBankBranch ?? '',
      amazonGiftCardEmail: expert?.bankData?.amazonGiftCardEmail ?? '',
    },
  })

  const handleCancel = () => navigate('/expert/personal-data')

  const handleSubmit = (data: FormsValues) => {
    const formattedData: UpdateExpertInternationalParams = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      linkedin: data.linkedin ?? undefined,
      city: data.city,
      state: data.state,
      country: data.country,
      bankData: {
        swiftCode: data.swiftCode,
        bankAccountNumber: data.bankAccountNumber,
        internationalBankBranch: data.internationalBankBranch,
        amazonGiftCardEmail: data.amazonGiftCardEmail,
      },
    }

    mutate(
      { id: expert!.id, data: formattedData },
      {
        onSuccess(result) {
          queryClient.invalidateQueries('expert')
          queryClient.setQueryData(['expert', expert!.id, 'bank-data'], {
            bank: result.bankData?.bank,
            currentAccount: result.bankData?.currentAccount,
            internationalBankBranch: result.bankData?.internationalBankBranch,
            swiftCode: result.bankData?.swiftCode,
            bankAccountNumber: result.bankData?.bankAccountNumber,
            amazonGiftCardEmail: result.bankData?.amazonGiftCardEmail,
          })
          navigate('/expert/personal-data')
          toast.success(
            formatMessage({ id: 'DATA_SUCCESSFULLY_SAVED' }),
            TOAST_SUCCESS_DEFAULT_OPTIONS
          )
        },
        onError(error) {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
        },
      }
    )
  }

  return {
    form,
    currentStep,
    isExpertInternationalDataSubmitting,
    setCurrentStep,
    handleCancel,
    handleSubmit,
  }
}
